import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../api/client';
import endpoints from '../../api/endpoints';
import LoadingSpinner from '../LoadingSpinner';
import { Eye, Trash2, Search, Download, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';

const ConversationsTab = () => {
  const [selectedThread, setSelectedThread] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const { data: threadsData, isLoading, refetch } = useQuery(
    ['admin-conversations', filterUser],
    () => {
      const params = new URLSearchParams();
      if (filterUser) params.append('userId', filterUser);
      return api.get(`${endpoints.conversations.list}?${params}`).then(res => res.data);
    }
  );

  const { data: usersData } = useQuery(
    'admin-users-list',
    () => api.get(endpoints.auth.users()).then(res => res.data)
  );

  const handleViewThread = async (threadId) => {
    try {
      const response = await api.get(endpoints.conversations.get(threadId));
      setSelectedThread(response.data.thread);
    } catch (error) {
      console.error('Error fetching thread:', error);
      toast.error('Failed to load conversation');
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this conversation? This will remove it from the database completely and cannot be undone.')) {
      return;
    }

    try {
      await api.delete(endpoints.conversations.delete(threadId));
      toast.success('Conversation permanently deleted');
      refetch();
      if (selectedThread?._id === threadId) {
        setSelectedThread(null);
      }
    } catch (error) {
      console.error('Error deleting thread:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const downloadConversation = (thread) => {
    const blob = new Blob([thread.messages], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${thread._id}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getUserName = (userId) => {
    const user = usersData?.users?.find(u => u._id === userId || u.id === userId);
    return user?.name || user?.username || 'Unknown User';
  };

  const filteredThreads = threadsData?.threads?.filter(thread => {
    if (!searchTerm) return true;
    const userName = getUserName(thread.userId).toLowerCase();
    const firstQuery = thread.metadata?.firstQuery?.toLowerCase() || '';
    return userName.includes(searchTerm.toLowerCase()) || firstQuery.includes(searchTerm.toLowerCase());
  }) || [];

  if (isLoading) {
    return <LoadingSpinner text="Loading conversations..." />;
  }

  return (
    <div className="conversations-tab">
      <div className="tab-section-header">
        <h2 className="tab-section-title">Conversation History</h2>
      </div>

      <div className="filters-bar">
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            className="filter-input"
            placeholder="Search by user or query..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '40px', width: '100%' }}
          />
        </div>
        
        <select
          className="filter-select"
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        >
          <option value="">All Users</option>
          {usersData?.users?.map(user => (
            <option key={user._id || user.id} value={user._id || user.id}>
              {user.name || user.username}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedThread ? '1fr 2fr' : '1fr', gap: 'var(--spacing-lg)' }}>
        <div>
          {filteredThreads.length === 0 ? (
            <div className="empty-state">
              <MessageSquare size={48} className="empty-state-icon" />
              <h3 className="empty-state-title">No Conversations</h3>
              <p className="empty-state-text">No conversation threads found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Started</th>
                  <th>Messages</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredThreads.map(thread => (
                  <tr key={thread._id} style={{ background: selectedThread?._id === thread._id ? 'var(--color-background)' : 'transparent' }}>
                    <td>
                      <strong>{getUserName(thread.userId)}</strong>
                      <br />
                      <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                        {thread.metadata?.firstQuery?.substring(0, 30)}...
                      </small>
                    </td>
                    <td>
                      {new Date(thread.threadStarted).toLocaleDateString()}
                      <br />
                      <small style={{ color: 'var(--color-text-secondary)' }}>
                        {new Date(thread.threadStarted).toLocaleTimeString()}
                      </small>
                    </td>
                    <td>{thread.messagesCount}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="action-button"
                          onClick={() => handleViewThread(thread._id)}
                          title="View conversation"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="action-button danger"
                          onClick={() => handleDeleteThread(thread._id)}
                          title="Delete conversation"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedThread && (
          <div className="conversation-viewer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.125rem' }}>
                  {getUserName(selectedThread.userId)}'s Conversation
                </h3>
                <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  {selectedThread.messagesCount} messages • {selectedThread.metadata?.totalTokensUsed || 0} tokens
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="action-button"
                  onClick={() => downloadConversation(selectedThread)}
                  title="Download as markdown"
                >
                  <Download size={14} /> Download
                </button>
                <button
                  className="action-button"
                  onClick={() => setSelectedThread(null)}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ 
              background: 'var(--color-background)', 
              borderRadius: 'var(--radius-md)', 
              padding: 'var(--spacing-lg)', 
              maxHeight: '600px', 
              overflowY: 'auto',
              border: '1px solid var(--color-border)'
            }}>
              <ReactMarkdown
                components={{
                  h2: ({node, ...props}) => <h2 style={{ 
                    color: 'var(--color-primary)', 
                    fontSize: '0.875rem', 
                    fontWeight: '600',
                    margin: '16px 0 8px',
                    padding: '8px 12px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '4px'
                  }} {...props} />
                }}
              >
                {selectedThread.messages}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsTab;
